/**
 * Homepage translations - English
 */

export default {
  hero: {
    title: 'Intelligent Infrastructure · Connecting Asia-Pacific',
    subtitle: 'Global Data is committed to empowering enterprise digital transformation through leading technology solutions, building an intelligent and interconnected future',
    cta: 'Contact Us Now',
    learnMore: 'Learn More'
  },
  
  coreMetrics: {
    title: 'Core Metrics Dashboard',
    subtitle: 'Numbers speak, strength proves',
    systemAvailability: 'System Availability',
    serviceRegions: 'Service Regions',
    clientsServed: 'Clients Served',
    certifications: 'Certifications',
    highAvailability: 'High availability guarantee all year round',
    coveringAsiaPacific: 'Covering major Asia-Pacific markets',
    enterpriseClients: 'Total enterprise clients served',
    internationalCerts: 'International authoritative certifications'
  },
  
  serviceRegions: {
    title: 'Service Coverage Map',
    subtitle: 'Covering 11 APAC service regions. Click map nodes to view local service details.',
    viewAllRegions: 'View All Regions'
  },
  
  certifications: {
    title: 'Qualifications & Compliance Capabilities',
    subtitle: 'International standard certifications, multi-regional compliance guarantee',
    viewAll: 'View All Certifications',
    validUntil: 'Valid Until',
    issuedBy: 'Issued By',
    categories: {
      quality: 'Quality Management',
      security: 'Security',
      telecom: 'Telecommunications',
      technology: 'Technology Partners',
      privacy: 'Privacy & Compliance'
    }
  },
  
  caseStudies: {
    title: 'Recent Success Stories',
    subtitle: 'Real cases, proven strength',
    filterByIndustry: 'Filter by Industry',
    allIndustries: 'All Industries',
    finance: 'Finance',
    healthcare: 'Healthcare',
    manufacturing: 'Manufacturing',
    government: 'Government',
    retail: 'Retail',
    professional: 'Professional Services',
    education: 'Education',
    viewAllCases: 'View All Cases',
    viewDetail: 'View Details',
    outcome: 'Project Outcomes',
    industry: 'Industry',
    region: 'Region',
    noResults: 'No {industry} cases are available yet. Please check other industries.',
    noCases: 'No cases available.'
  },
  
  testimonials: {
    title: 'Customer Testimonials',
    subtitle: 'Hear what our clients say',
    rating: 'Rating'
  },
  
  regionalContact: {
    title: 'Contact Us',
    subtitle: '11 service regions, always at your service',
    office: 'Service Center',
    phone: 'Phone',
    email: 'Email',
    businessHours: 'Business Hours',
    viewContactPage: 'View Complete Contact Information'
  },
  
  cta: {
    title: 'Experience Our Products Now',
    subtitle: 'Global Data is committed to providing you with cutting-edge technology solutions. Our products have helped hundreds of enterprises achieve digital transformation, improve operational efficiency, and drive business growth.',
    contact: 'Contact Us',
    trialNow: 'Start Free Trial'
  },
  
  news: {
    latest: 'Latest News',
    industry: 'Industry Insights',
    company: 'Company News',
    item1: {
      content: 'Global Data partners with leading global AI company to explore new frontiers in AI.',
      date: 'October 26, 2023'
    },
    item2: {
      content: 'Global Data releases 2024 Technology Trends Report, focusing on cloud computing and big data.',
      date: 'October 20, 2023'
    },
    item3: {
      content: 'Global Data honored as "Best Technology Innovation Enterprise of the Year".',
      date: 'October 15, 2023'
    }
  },
  
  insights: {
    title: 'Featured Trends & Insights',
    digitalTransformation: {
      title: 'Digital Transformation: The New Engine of Business Growth',
      description: 'Deep dive into how digital transformation is reshaping the business landscape and how Global Data helps enterprises achieve efficient transformation.'
    },
    aiFuture: {
      title: 'AI and the Future: Infinite Possibilities in the Intelligent Era',
      description: 'Explore the latest advances in artificial intelligence and its application prospects across industries, featuring Global Data AI solutions.'
    },
    bigData: {
      title: 'Big Data: The Source of Business Value Insights',
      description: 'Explore how big data empowers enterprise decision-making and how Global Data provides comprehensive data analysis solutions.'
    }
  },

  coreServices: {
    kicker: 'Service Portfolio',
    title: 'Core Services',
    subtitle: 'Enterprise-grade delivery with unified SLAs, cross-regional coordination, and local execution across the digital lifecycle.',
    viewDetail: 'Learn more',
    viewAllServices: 'View all services',
    items: [
      {
        sectionId: 'digital-workplace',
        eyebrow: 'Digital Workplace',
        title: 'Desktop & endpoint experience',
        description: 'Unified endpoints, accounts, permissions, and collaboration tools—from onboarding to offboarding, remote work, and full IT asset lifecycle management.'
      },
      {
        sectionId: 'data-center-network',
        eyebrow: 'Data center & network',
        title: 'Corporate & store connectivity',
        description: 'LAN/WAN/WiFi, switches and firewalls, plus data center and cabling operations—keeping mission-critical systems stable and resilient.'
      },
      {
        sectionId: 'cloud-operations',
        eyebrow: 'Cloud & cloud operations',
        title: 'Cloud architecture & FinOps',
        description: 'Public/hybrid cloud design, migration, day-2 operations, security monitoring, and cost optimization for agility at scale.'
      },
      {
        sectionId: 'retail-store-ops',
        eyebrow: 'Retail store IT',
        title: 'Standardized multi-store delivery',
        description: 'Standardized runbooks, POS and in-store network support, new-store rollouts, relocations, and upgrades—minimizing business disruption.'
      }
    ]
  },

  productExtensions: {
    title: 'Digital capabilities',
    subtitle: 'Beyond smart ticketing and AI analytics, we extend into CRM engineering, marketing automation, mini programs, AI knowledge bases, and office intelligent agents—moving operations toward data-driven execution.',
    viewAll: 'View Product Capabilities',
    items: [
      {
        title: 'CRM Software Development',
        tagline: 'Membership systems and consumer user operations',
        description: 'Build a consumer-facing membership CRM for member profiles, segmentation, loyalty points, benefits, engagement, and repeat-purchase operations.'
      },
      {
        title: 'MA Marketing Automation',
        tagline: 'Automated engagement and conversion growth',
        description: 'Connect your website, CRM, email, and messaging channels to run closed-loop lead nurturing journeys.'
      },
      {
        title: 'Mini Program Development',
        tagline: 'Lightweight service entry',
        description: 'Deliver tailored mini programs for appointments, repair requests, inquiries, and member services.'
      },
      {
        title: 'AI Knowledge Base',
        tagline: 'Knowledge capture and Q&A',
        description: 'Unify FAQs, SOPs, ticket experience, and product materials with semantic retrieval and permission control.'
      },
      {
        title: 'Office Intelligent Agents',
        tagline: 'Office workflow automation',
        description: 'Connect email, approvals, meeting notes, task flows, and knowledge lookup to improve internal collaboration.'
      }
    ]
  },
  
  mission: {
    title: 'Our Mission',
    description: 'Global Data is committed to becoming a leading global technology service provider. Through continuous innovation and excellent service, we empower clients to achieve business growth and digital transformation, building a more intelligent and interconnected future together. We believe in the power of technology and use it as a driving force to create value for society.'
  },
  
  expertise: {
    title: 'Our Areas of Expertise',
    bigData: {
      title: 'Big Data & Analytics',
      description: 'From data collection to deep analysis, helping enterprises mine value from massive data to drive intelligent decision-making.'
    },
    ai: {
      title: 'Artificial Intelligence & Machine Learning',
      description: 'Develop and deploy customized AI solutions, including natural language processing, computer vision, and intelligent automation.'
    },
    itConsulting: {
      title: 'IT Consulting & Management',
      description: 'Provide strategic IT consulting, project management, and IT operations optimization to ensure technology investments align with business objectives.'
    }
  },
  
  services: {
    title: 'Our Services',
    ticketSystem: {
      title: 'Smart Ticket System',
      subtitle: 'Efficiently manage your service requests and issues',
      description: 'Our smart ticket system is designed to streamline your service request process with full visualization from submission to resolution. You can track ticket status in real-time, understand processing progress, and communicate seamlessly with our technical team.',
      features: {
        realTimeTracking: 'Real-time Status Tracking',
        multiChannel: 'Multi-channel Submission',
        smartAssignment: 'Smart Assignment',
        historyQuery: 'History Query'
      },
      progressTitle: 'Ticket Processing Progress Example'
    },
    aiSolutions: {
      title: 'AI-Powered Solutions',
      subtitle: 'Leveraging artificial intelligence to drive business growth',
      description: 'We integrate cutting-edge artificial intelligence technology into your business processes, providing customized AI solutions including intelligent customer service, data prediction, automated decision-making, etc., to help you improve efficiency and optimize user experience.',
      features: {
        chatbot: 'Intelligent Chatbot',
        biAnalytics: 'Business Intelligence Analytics',
        recommendation: 'Personalized Recommendations',
        security: 'Intelligent Security Protection'
      },
      ctaButton: 'Explore AI Solutions'
    }
  }
}
